import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Download, RefreshCw, SlidersHorizontal, Wallet,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import { FilterBar } from './clientPerformance/FilterBar';
import { SellThroughView } from './clientPerformance/SellThroughView';
import { ScorecardView } from './clientPerformance/ScorecardView';
import { SeasonalityView } from './clientPerformance/SeasonalityView';
import { ReturnsByProductView } from './clientPerformance/ReturnsByProductView';
import { ThresholdsDialog } from './clientPerformance/ThresholdsDialog';
import { DATE_PRESETS, toParams } from './clientPerformance/metrics';

const TABS = [
  { value: 'sell-through', label: 'Sell-through & returns' },
  { value: 'scorecards', label: 'Client scorecards' },
  { value: 'seasonality', label: 'Seasonality' },
  { value: 'returns', label: 'Returns by product' },
];

const EXPORT_VIEW = {
  'sell-through': 'sell-through',
  scorecards: 'scorecard-monthly',
  seasonality: 'seasonality',
  returns: 'returns-by-product',
};

export default function ClientPerformance() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState(searchParams.get('tab') || 'sell-through');
  const [clientId, setClientId] = useState(searchParams.get('client') || '');
  const [options, setOptions] = useState(null);
  const [thresholdsOpen, setThresholdsOpen] = useState(false);
  const [granularity, setGranularity] = useState('month');
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState(() => {
    const preset = DATE_PRESETS.find((p) => p.key === 'last_90');
    return {
      preset: 'last_90',
      ...preset.range(),
      clientIds: searchParams.get('client') ? [searchParams.get('client')] : [],
      productIds: [],
      categories: [],
      weekday: '',
      segment: 'all',
      tierId: '',
    };
  });

  const loadOptions = useCallback(async () => {
    try {
      setOptions(await adminService.getClientPerformanceFilters());
    } catch (error) {
      toast.error('Failed to load filter options');
    }
  }, []);

  useEffect(() => { loadOptions(); }, [loadOptions, reloadKey]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    if (clientId) next.set('client', clientId); else next.delete('client');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, clientId]);

  const params = useMemo(() => ({ ...toParams(filters), _k: reloadKey }), [filters, reloadKey]);
  const requestParams = useMemo(() => {
    const { _k, ...rest } = params;
    return rest;
  }, [params]);

  const openScorecard = (id) => {
    setClientId(id);
    setTab('scorecards');
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      await adminService.exportClientPerformance(
        EXPORT_VIEW[tab],
        requestParams,
        tab === 'scorecards' ? clientId : undefined,
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const rebuild = async () => {
    try {
      const result = await adminService.rebuildSalesHistory();
      toast.success(`Analytics rebuilt — ${result.rows_processed} rows rewritten`);
      setReloadKey((k) => k + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rebuild failed');
    }
  };

  return (
    <div className="space-y-5" data-testid="client-performance-page">
      <div className="flex items-start gap-3 flex-wrap">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/admin/reports')}
          data-testid="back-to-reports-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-[260px]">
          <h2 className="text-2xl font-bold text-gray-900">Client Performance</h2>
          <p className="text-sm text-gray-500 mt-1">
            What sells, what comes back, what it costs — and which clients are worth their terms.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/product-costs')}
            data-testid="open-product-costs-btn"
          >
            <Wallet className="h-4 w-4 mr-2" /> Product costs
          </Button>
          <Button
            variant="outline"
            onClick={() => setThresholdsOpen(true)}
            data-testid="open-thresholds-btn"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" /> Thresholds
          </Button>
          <Button variant="outline" onClick={rebuild} data-testid="rebuild-analytics-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Rebuild data
          </Button>
          <Button
            className="bg-amber-500 hover:bg-amber-600"
            onClick={exportCsv}
            disabled={exporting || (tab === 'scorecards' && !clientId)}
            data-testid="export-client-performance-btn"
          >
            <Download className="h-4 w-4 mr-2" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>
      </div>

      <FilterBar
        filters={filters}
        setFilters={setFilters}
        options={options}
        completeness={options?.completeness}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full h-auto">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className="text-xs sm:text-sm py-2"
              data-testid={`tab-${t.value}`}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === 'sell-through' && (
        <SellThroughView
          params={requestParams}
          granularity={granularity}
          setGranularity={setGranularity}
          onOpenScorecard={openScorecard}
        />
      )}
      {tab === 'scorecards' && (
        <ScorecardView
          params={requestParams}
          clients={options?.clients}
          clientId={clientId}
          setClientId={setClientId}
        />
      )}
      {tab === 'seasonality' && <SeasonalityView params={requestParams} />}
      {tab === 'returns' && <ReturnsByProductView params={requestParams} />}

      <ThresholdsDialog
        open={thresholdsOpen}
        onOpenChange={setThresholdsOpen}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );
}
